import { useCallback, useEffect, useState } from "react";
import { ElementItemProps } from "../types";
import { useIntl } from "react-intl";
import { useDevice } from "../../../../utils/DeviceProvider";
import { Button, Grid, Skeleton, Typography } from "@mui/material";
import Info from "./Info";
import { TxtboxElement } from "./Txtbox";
import { useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";
import { isEqual } from "lodash-es";
import { useEditedColor } from "../../../../hooks/useEditedStyles";

export interface ORFIDLSV1Parameters {
  maxCount: number;
}

const ORFIDLSV1 = ({ configKey, maxCount, disabled }: Omit<ElementItemProps<ORFIDLSV1Parameters>, "elementKey" | "label">) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<string[] | null>(null);
  const [value, setValue] = useState<string[] | null>(null);

  const { formatMessage } = useIntl();
  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { openContextMenu } = useResetContextMenu();
  const { text: editedText } = useEditedColor();

  const onChange = useCallback(
    (value: string, index: number) => {
      if (configIndex === null) {
        return;
      }
      currentValue[configKey].items[configIndex].value[index] = value;
      setCurrentValue(currentValue);
    },
    [setCurrentValue, currentValue, configKey, configIndex]
  );

  const resetList = useCallback(() => {
    if (configIndex === null) {
      return;
    }
    currentValue[configKey].items[configIndex].value = saved;
    setCurrentValue(currentValue);
  }, [configIndex, configKey, currentValue, saved, setCurrentValue]);

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(({ type }: { type: string }) => type === "ORFIDLSV1") ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex].value ?? null;
    setSaved(svd);
    const val = currentValue[configKey].items[configIndex]?.value ?? [];
    setValue(val);
  }, [active, currentValue, config, configIndex, configKey]);

  if (value === null) {
    return (
      <Grid container spacing={1}>
        <Grid item>
          <Skeleton variant="text" sx={{ width: 20, borderRadius: 1, paddingBottom: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 200, borderRadius: 1 }} />
        </Grid>
      </Grid>
    );
  }

  if (configIndex === -1) {
    return <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>;
  }

  const edited = !isEqual(saved, value);

  return (
    <Grid container spacing={1} sx={{ flexDirection: { xs: "column", sm: edited ? "row-reverse" : "row" } }}>
      {edited ? (
        <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "flex-start", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
          <Button sx={{ color: editedText }} onClick={resetList}>
            {formatMessage({ defaultMessage: "Reset the full RFID list" })}
          </Button>
        </Grid>
      ) : null}
      <Grid item container spacing={1} xs={12} sm={6}>
        {Array.from({ length: maxCount }).map((_, index) => (
          <Grid item container spacing={1} key={`rfid-${index}`}>
            <Grid item>
              <Info text={`${index + 1}.`} inlineTitle />
            </Grid>
            <Grid item>
              <RfidItem
                key={`rfid-${index}-${value[index]}`}
                value={value[index]}
                index={index}
                onBlur={onChange}
                saved={saved}
                disabled={disabled}
                onContextMenu={
                  saved && saved.length > index && saved[index] !== value[index]
                    ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.${index}`)
                    : undefined
                }
              />
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

const RfidItem = ({
  value,
  index,
  onBlur,
  saved,
  disabled,
  onContextMenu,
}: {
  value: string;
  index: number;
  onBlur: (value: string, index: number) => void;
  saved: string[] | null;
  disabled?: boolean;
  onContextMenu?: React.MouseEventHandler<HTMLElement>;
}) => {
  const [inputValue, setInputValue] = useState(value ?? "");

  const handleOnBlur = useCallback(() => {
    onBlur(inputValue, index);
  }, [inputValue, index, onBlur]);

  return (
    <TxtboxElement
      value={inputValue}
      label=""
      onChange={setInputValue}
      onBlur={handleOnBlur}
      edited={(saved?.[index] || "") !== inputValue}
      enabledCharacters="0-9"
      disabled={disabled}
      onContextMenu={onContextMenu}
    />
  );
};

export default ORFIDLSV1;
